import { userApi } from '~/api'
import type {
  User,
  Auth,
  Finance,
  Banned, 
  LoginResponse, 
  VerifyLoginParams, 
  SetPwdParams,
  RegisterParams,
  ResetPwdParams
} from '@xianlaibang/shared/types'

import {
  toUser,
  toAuth,
  toFinance,
  toBanned
} from '@xianlaibang/shared/transition'

export const useUserStore = defineStore('userStore', {
  state: () => ({
    // 用户信息
    user: toUser({}) as User,
    // 用户认证信息
    auth: toAuth({}) as Auth,
    // 用户财务信息
    finance: toFinance({}) as Finance,
    // 用户封禁信息
    banned: toBanned({}) as Banned,
  }),
  getters: {
    isLogged: () => !!useCookie('auth_token').value,
    token: () => { 
      return useCookie('auth_token').value
    }
  },
  actions: {
    setToken(token: string, expires_in: number = 60 * 60 * 24 * 7) {
      const config = useRuntimeConfig()
      if (config.app.buildId === 'dev') {
        useCookie('auth_token', {
          default: () => '',
          maxAge: expires_in,
          // httpOnly: true,// 防止XSS攻击
          // secure: true, // 仅通过HTTPS传输
          // sameSite: 'strict' // 防止CSRF攻击
          // domain: config.public.cookieDomain, // 设置cookie的域
        }).value = token
      } else {
        useCookie('auth_token', {
          default: () => '',
          maxAge: expires_in,
          // httpOnly: true,// 防止XSS攻击
          // secure: true, // 仅通过HTTPS传输
          // sameSite: 'strict' // 防止CSRF攻击
          domain: config.public.cookieDomain, // 设置cookie的域
        }).value = token
      }
    },
    // 获取用户信息
    async getUserInfo(): Promise<User> {
      // 判断用户是否登录
      try {
        // 调用后端API获取用户信息
        const { data } = await userApi.getUserInfo()
        this.user = toUser(data ?? {})
        return this.user
      } catch (error) {
        console.error('获取用户信息失败:', error)
        throw error
      }
    },
    // 获取认证信息
    async getAuthInfo(): Promise<Auth> {
      try {
        // 调用后端API获取用户信息
        const { data } = await userApi.getAuthInfo()
        // 后端返回的data有可能是null，需要处理
        this.auth = toAuth(data ?? {})
        return this.auth
      } catch (error) {
        console.error('获取用户信息失败:', error)
        throw error
      }
    },
    // 获取财务信息
    async getFinanceInfo(): Promise<Finance> {
      try {
        // 调用后端API获取用户信息
        const { data } = await userApi.getFinanceInfo()
        // 后端返回的data有可能是null，需要处理
        this.finance = toFinance(data ?? {})
        return this.finance
      } catch (error) {
        console.error('获取用户信息失败:', error)
        throw error
      }
    },
    async getBannedInfo(secretKeyCode: number): Promise<Banned> {
      try {
        // 调用后端API获取用户信息
        const { data } = await userApi.getBannedInfo(secretKeyCode, '0')
        // 后端返回的data有可能是null，需要处理
        this.banned = toBanned(data ?? {})
        return this.banned
      } catch (error) {
        console.error('获取用户信息失败:', error)
        throw error
      }
    },
    // 密码登录
    async login(credentials: { userName: string; password: string }) {
      try {
        // 调用后端API进行登录
        const response  = await userApi.login(credentials)
        this.setToken(response.data.access_token, response.data.expires_in * 60)
        // 更新用户信息
        return response
      } catch (error) {
        console.error('登录失败:', error)
        throw error
      }
    },
    // 验证码登录
    async verifyLogin(credentials: VerifyLoginParams) {
      try {
        // 调用后端API进行登录
        // const response: LoginResponse  = await $fetch('/api/auth/login', {
        //   method: 'POST',
        //   body: credentials
        // })
         const response: LoginResponse  = {
          access_token: '1234567890',
          expires_in: 720,
         }
         
        // 假设API返回格式为 { token: string, user: { id, name, email } }
        this.setToken(response.access_token, response.expires_in * 60)

        return { success: true }
      } catch (error) {
        console.error('Login failed:', error)
        return { success: false, error }
      }
    },
    // 用户注册
    async register(credentials: RegisterParams): Promise<LoginResponse> {
      return new Promise(async (resolve, reject) => {
        try {
          // 调用后端API进行登录
          const response = await userApi.register(credentials)
          if (response.code === 200) {
            this.setToken(response.data.access_token, response.data.expires_in * 60)
            resolve(response.data)
          } else {
            reject(response)
          }
        } catch (error) {
          reject(error)
        }
      })
    },
    // 用户设置密码
    async setPwd(credentials: SetPwdParams) {
      return new Promise(async (resolve, reject) => {
        try {
          // 调用后端API进行登录
          const response = await userApi.resetPassword(credentials)
          if (response.code === 200) {
            this.setToken(response.data.access_token, response.data.expires_in * 60)
            resolve(response.data)
          } else {
            reject(response)
          }
        } catch (error) {
          reject(error)
        }
      })
    },
    // 仅清除本地登录态（不调 logout 接口，对齐移动端 ClearData）
    clearLocalAuth() {
      this.setToken('')
      this.user = toUser({})
      this.auth = toAuth({})
      this.finance = toFinance({})
      this.banned = toBanned({})
    },
    // 用户退出登录
    async logout() {
      // 请求后端API进行登出操作
      await userApi.logout()
      this.clearLocalAuth()
      // 使用刷新页面的方法确保所有状态重置
      // setTimeout(() => {window.location.reload()}, 100)
      // 或者使用导航的方法重定向到登录页
      await navigateTo('/login')
    },
    // 判断用户是否已登录
    checkLogin() {
      if (!this.isLogged) {
        navigateTo('/login')
      }
    }
  }
})
